<!-- Copyright: Seán I. O'Donoghue -->

<template>
  <AppFormWrapper id="sign-in-name-form" @submit.prevent="onSubmit">
    <h2>Your Details</h2>
    <input
      v-model="firstName"
      type="text"
      placeholder="First name"
      autocomplete="given-name"
      required
    />
    <input
      v-model="lastName"
      type="text"
      placeholder="Last name"
      autocomplete="family-name"
      required
    />
    <input
      v-model="affiliation"
      type="text"
      placeholder="Affiliation (e.g. university or company)"
      autocomplete="organization"
    />
    <p v-if="errorMessage" class="error">{{ useTitleCase(errorMessage) }}</p>
  </AppFormWrapper>
</template>

<script setup>
import {computed, ref, watch} from "vue";
import {useStorage} from "@vueuse/core";
import {useClerk, useSignUp} from "@clerk/vue";
import AppFormWrapper from "@/components/App/Form/Wrapper";
import {useTitleCase} from "@/composables/useTitleCase";
import {useSaveIsSubscribed} from "@/composables/useSaveIsSubscribed";

const emit = defineEmits(["success", "update:canSubmit"]);

const {signUp} = useSignUp();
const clerk = useClerk();

const firstName = useStorage("sign-in-firstName", "");
const lastName = useStorage("sign-in-lastName", "");
const affiliation = ref("");
const {save} = useSaveIsSubscribed();
const loading = ref(false);
const errorMessage = ref("");
const canSubmit = computed(() => !loading.value && firstName.value && lastName.value);
watch(canSubmit, (val) => emit("update:canSubmit", val), {immediate: true});

// Clerk asked for the names before finishing the sign-up (missing_requirements)
async function fetchFinishedSignUp(names) {
  const result = await signUp.value.update(names);
  const status = result.status;
  if (status !== "complete") throw new Error(`Sign-up incomplete (status: ${status})`);
  await clerk.value.setActive({session: result.createdSessionId});
}

// a new account, signed in once its code was accepted (Seán, 2026-11-02): its names, and its
// affiliation (publicMetadata, as Profile saves it: useSaveIsSubscribed)
async function saveDetails(names) {
  const user = clerk.value.user;
  if (!user) await fetchFinishedSignUp(names);
  else await user.update(names);
  const text = affiliation.value.trim();
  if (text) await save(clerk.value.user, {affiliation: text});
}

async function onSubmit() {
  errorMessage.value = "";
  loading.value = true;
  try {
    await saveDetails({firstName: firstName.value.trim(), lastName: lastName.value.trim()});
    emit("success");
  } catch (error) {
    errorMessage.value = error?.errors?.[0]?.message || error?.message
      || "An error occurred. Please try again.";
  } finally {
    loading.value = false;
  }
}
</script>

